import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import WaveDivider from './home/WaveDivider';
import { useLanguage } from '../contexts/LanguageContext';
import { trackCTAClick, pushToDataLayer } from '../config/gtm';
import { waitlistService } from '../services/api';

type JourneyFilter = 'todos' | 'libros' | 'retos' | 'insignias' | 'debate';

const Home: React.FC = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const t = (es: string, en: string) => (language === 'es' ? es : en);

    const [journeyFilter, setJourneyFilter] = useState<JourneyFilter>('todos');
    const [newsletterEmail, setNewsletterEmail] = useState('');
    const [newsletterPrivacy, setNewsletterPrivacy] = useState(false);
    const [newsletterSuccess, setNewsletterSuccess] = useState(false);
    const [newsletterLoading, setNewsletterLoading] = useState(false);
    const [newsletterError, setNewsletterError] = useState<string | null>(null);

    const featuredBooks = [
        { num: 1, title: t('Aprender a Aprender', 'Learning to Learn'), img: '/images/portadas/portadalibro1.png', path: '/libros/1' },
        { num: 2, title: t('Aprender a Pensar', 'Learning to Think'), img: '/images/portadas/portadalibro2.png', path: '/libros/2' },
        { num: 3, title: t('Aprender a Debatir', 'Learning to Debate'), img: '/images/portadas/portadalibro3.png', path: '/libros/3' },
    ];

    const journeySteps = [
        {
            id: 'libros' as const,
            filter: 'libros' as JourneyFilter,
            step: 1,
            icon: '📖',
            title: t('Aprende con historias', 'Learn with stories'),
            desc: t(
                'Libros y aventuras que enseñan sabiduría, valores y pensamiento.',
                'Books and adventures that teach wisdom, values and thinking.'
            ),
            path: '/libros/1',
        },
        {
            id: 'retos' as const,
            filter: 'retos' as JourneyFilter,
            step: 2,
            icon: '⭐',
            title: t('Practica con retos', 'Practice with challenges'),
            desc: t(
                'Actividades en casa para crear hábitos, reflexión y conexión familiar.',
                'At-home activities to build habits, reflection and family connection.'
            ),
            path: '/innovaciones?section=4',
        },
        {
            id: 'insignias' as const,
            filter: 'insignias' as JourneyFilter,
            step: 3,
            icon: '🛡️',
            title: t('Gana insignias', 'Earn badges'),
            desc: t(
                'Cada logro se convierte en insignias, títulos y avance visible.',
                'Every achievement becomes badges, titles and visible progress.'
            ),
            path: '/gamification',
        },
        {
            id: 'debate' as const,
            filter: 'debate' as JourneyFilter,
            step: 4,
            icon: '🚀',
            title: t('Debate y crea', 'Debate and create'),
            desc: t(
                'Defiende ideas, completa desafíos y descubre talentos con propósito.',
                'Defend ideas, complete challenges and discover talents with purpose.'
            ),
            path: '/consejo-sabios',
        },
    ];

    const visibleSteps =
        journeyFilter === 'todos'
            ? journeySteps
            : journeySteps.filter((s) => s.filter === journeyFilter);

    const filterPills: { key: JourneyFilter; label: string }[] = [
        { key: 'todos', label: t('Todos', 'All') },
        { key: 'libros', label: t('Libros', 'Books') },
        { key: 'retos', label: t('Retos', 'Challenges') },
        { key: 'insignias', label: t('Insignias', 'Badges') },
        { key: 'debate', label: t('App Debate', 'Debate App') },
    ];

    const featureBar = [
        { label: t('Libros', 'Books'), icon: '📚', color: 'bg-princonser-purple-muted text-princonser-purple', path: '#libros-section' },
        { label: t('Retos', 'Challenges'), icon: '🏆', color: 'bg-orange-100 text-orange-600', path: '/innovaciones?section=4' },
        { label: t('Insignias', 'Badges'), icon: '🛡️', color: 'bg-pink-100 text-pink-600', path: '/gamification' },
        { label: t('Actividades en familia', 'Family activities'), icon: '👨‍👩‍👧', color: 'bg-emerald-100 text-emerald-600', path: '/support-materials' },
        { label: t('App Debate', 'Debate App'), icon: '💬', color: 'bg-princonser-purple-muted text-princonser-purple', path: '/consejo-sabios' },
    ];

    const bookIncludes = [
        {
            icon: '📖',
            title: t('Historia con propósito', 'Story with purpose'),
            desc: t('Cuentos que enseñan valores y reflexión desde el primer momento.', 'Stories that teach values and reflection from the start.'),
        },
        {
            icon: '👨‍👩‍👧',
            title: t('Retos en familia', 'Family challenges'),
            desc: t('Actividades para conectar, dialogar y crecer juntos en casa.', 'Activities to connect, talk and grow together at home.'),
        },
        {
            icon: '🏅',
            title: t('Insignias y progreso', 'Badges and progress'),
            desc: t('Cada avance se celebra con insignias y reconocimiento visible.', 'Every step forward is celebrated with badges and visible recognition.'),
        },
    ];

    const whyFeatures = [
        { icon: '🧭', color: 'text-emerald-500 bg-emerald-50', title: t('Método con propósito', 'Method with purpose'), desc: t('Filosofía Princonser aplicada paso a paso.', 'Princonser Philosophy applied step by step.') },
        { icon: '🏆', color: 'text-amber-500 bg-amber-50', title: t('Gamificación real', 'Real gamification'), desc: t('Retos, insignias y motivación constante.', 'Challenges, badges and steady motivation.') },
        { icon: '🌐', color: 'text-princonser-purple bg-princonser-purple-muted', title: t('Comunidad global', 'Global community'), desc: t('Debates y conexión con familias y escuelas.', 'Debates and connection with families and schools.') },
        { icon: '📖', color: 'text-rose-500 bg-rose-50', title: t('Libros integrales', 'Comprehensive books'), desc: t('8 historias con guías para padres y maestros.', '8 stories with guides for parents and teachers.') },
    ];

    const parentBenefits = [
        t('Menos conflictos por pantallas', 'Fewer screen conflicts'),
        t('Más conexión familiar', 'More family connection'),
        t('Hijos con pensamiento crítico', 'Children with critical thinking'),
        t('Recursos listos para usar', 'Ready-to-use resources'),
    ];

    const schoolBenefits = [
        t('Currículo complementario', 'Complementary curriculum'),
        t('Evidencias de impacto', 'Evidence of impact'),
        t('Formación docente incluida', 'Teacher training included'),
        t('Implementación flexible', 'Flexible implementation'),
    ];

    const newsletterBenefits = [
        { icon: '📚', color: 'bg-princonser-purple', label: t('Contenido exclusivo', 'Exclusive content') },
        { icon: '⭐', color: 'bg-princonser-gold', label: t('Tips educativos y prácticos', 'Practical educational tips') },
        { icon: '🎁', color: 'bg-princonser-green', label: t('Recursos gratuitos para tu familia', 'Free resources for your family') },
        { icon: '❤️', color: 'bg-princonser-pink', label: t('Novedades sobre nuestros libros', 'News about our books') },
    ];

    const scrollTo = (hash: string) => {
        if (hash.startsWith('#')) {
            document.querySelector(hash)?.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className="min-h-screen bg-white w-full overflow-x-hidden">
            <Navbar />

            <main>
                {/* ——— Hero ——— */}
                <section className="relative min-h-[92vh] flex items-center bg-princonser-hero pt-20 pb-24 overflow-hidden">
                    <div className="absolute inset-0 opacity-20 pointer-events-none" aria-hidden>
                        <span className="absolute top-24 left-[10%] text-4xl">✈️</span>
                        <span className="absolute top-32 right-[15%] text-2xl">⭐</span>
                        <span className="absolute top-48 left-[20%] text-xl">💡</span>
                        <span className="absolute bottom-40 right-[25%] text-2xl">❤️</span>
                        <span className="absolute bottom-32 left-[30%] text-3xl">📚</span>
                    </div>

                    <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-center">
                            <div className="text-white">
                                <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-princonser-purple-dark/50 border border-white/25 text-sm font-medium mb-6">
                                    <span className="text-pink-300">♥</span>
                                    {t('Donde aprender se convierte en sabiduría', 'Where learning becomes wisdom')}
                                </span>
                                <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[3.25rem] font-extrabold leading-tight mb-6">
                                    {t('Formando niños', 'Raising')}{' '}
                                    <span className="text-princonser-green-bright">{t('sabios', 'wise')}</span>{' '}
                                    {t('con', 'with')}{' '}
                                    <span className="bg-gradient-to-r from-orange-400 via-pink-400 to-rose-400 bg-clip-text text-transparent">
                                        {t('propósito', 'purpose')}
                                    </span>
                                </h1>
                                <p className="text-base sm:text-lg text-white/90 leading-relaxed mb-8 max-w-xl">
                                    {t(
                                        'Princonser Kids conecta historias, retos, insignias y debate para que padres y escuelas formen niños con valores, pensamiento crítico y alegría por aprender.',
                                        'Princonser Kids connects stories, challenges, badges and debate so parents and schools raise children with values, critical thinking and joy for learning.'
                                    )}
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            trackCTAClick('Descubrir Princonser Kids', 'Hero', 'button');
                                            navigate('/que-es-princonser');
                                        }}
                                        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-bold text-white bg-princonser-green hover:bg-princonser-green-hover shadow-lg shadow-princonser-green transition-all hover:scale-[1.02]"
                                    >
                                        {t('Descubrir Princonser Kids', 'Discover Princonser Kids')}
                                        <span className="w-8 h-8 rounded-full bg-white/25 flex items-center justify-center text-sm">→</span>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            trackCTAClick('Explorar los libros', 'Hero', 'button');
                                            navigate('/libros/1');
                                        }}
                                        className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-semibold text-white border-2 border-white/90 hover:bg-white/10 transition-all"
                                    >
                                        {t('Explorar los libros', 'Explore the books')}
                                        <span className="w-8 h-8 rounded-full border border-white/50 flex items-center justify-center text-xs">▶</span>
                                    </button>
                                </div>
                            </div>

                            <div className="relative flex justify-center lg:justify-end">
                                <div className="relative w-full max-w-md lg:max-w-lg">
                                    <img
                                        src="/images/infosection/NinosArbol.png"
                                        alt={t('Niños aprendiendo con Princonser Kids', 'Children learning with Princonser Kids')}
                                        className="w-full h-auto object-contain drop-shadow-2xl rounded-2xl"
                                        onError={(e) => {
                                            const img = e.target as HTMLImageElement;
                                            if (!img.dataset.fallback) {
                                                img.dataset.fallback = '1';
                                                img.src = '/images/portadas/portadalibro1.png';
                                            }
                                        }}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0">
                        <WaveDivider fillClass="text-white" />
                    </div>
                </section>

                {/* ——— Barra de features ——— */}
                <section className="bg-white py-8 sm:py-10 border-b border-gray-100">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6 sm:gap-4">
                            {featureBar.map((item) => (
                                <button
                                    key={item.label}
                                    type="button"
                                    onClick={() => {
                                        if (item.path.startsWith('#')) scrollTo(item.path);
                                        else navigate(item.path);
                                    }}
                                    className="flex flex-col items-center gap-2 group"
                                >
                                    <div
                                        className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center text-2xl ${item.color} group-hover:scale-105 transition-transform shadow-sm`}
                                    >
                                        {item.icon}
                                    </div>
                                    <span className="text-xs sm:text-sm font-semibold text-princonser-dark text-center leading-tight">
                                        {item.label}
                                    </span>
                                </button>
                            ))}
                        </div>
                    </div>
                </section>

                {/* ——— El camino ——— */}
                <section id="experiencia" className="relative py-16 sm:py-20 bg-white overflow-hidden">
                    <span className="absolute top-8 left-8 text-4xl opacity-30 pointer-events-none" aria-hidden>☁️</span>
                    <span className="absolute top-12 right-12 text-3xl opacity-30 pointer-events-none" aria-hidden>🌈</span>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="text-center mb-10">
                            <span className="inline-block px-4 py-1 rounded-full bg-princonser-purple-muted text-princonser-purple text-sm font-semibold mb-4">
                                {t('Explora la experiencia', 'Explore the experience')}
                            </span>
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-princonser-navy mb-3">
                                {t('El camino para', 'The path to')}{' '}
                                <span className="text-princonser-purple">{t('crecer', 'grow')}</span>{' '}
                                {t('con sabiduría', 'with wisdom')}
                            </h2>
                            <p className="text-princonser-text-body max-w-2xl mx-auto text-sm sm:text-base">
                                {t(
                                    'Cada niño avanza paso a paso: lee, practica, gana insignias, debate y descubre sus talentos.',
                                    'Each child advances step by step: read, practice, earn badges, debate and discover their talents.'
                                )}
                            </p>
                        </div>

                        <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10">
                            {filterPills.map((pill) => (
                                <button
                                    key={pill.key}
                                    type="button"
                                    onClick={() => setJourneyFilter(pill.key)}
                                    className={`px-4 sm:px-5 py-2 rounded-full text-sm font-semibold transition-all ${
                                        journeyFilter === pill.key
                                            ? 'bg-princonser-purple text-white shadow-md'
                                            : 'bg-white text-princonser-text-body border border-gray-200 hover:border-princonser-purple-light'
                                    }`}
                                >
                                    {pill.label}
                                </button>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                            {visibleSteps.map((step) => (
                                <article
                                    key={step.id}
                                    className="bg-white rounded-2xl p-6 shadow-lg shadow-princonser-card border border-gray-100 flex flex-col hover:shadow-xl transition-shadow"
                                >
                                    <div className="text-5xl mb-4 text-center">{step.icon}</div>
                                    <h3 className="font-bold text-princonser-navy text-center mb-2">{step.title}</h3>
                                    <p className="text-sm text-princonser-text-body text-center flex-grow mb-6">{step.desc}</p>
                                    <div className="flex items-center justify-between mt-auto">
                                        <span className="text-xs font-semibold text-princonser-purple bg-princonser-purple-muted px-3 py-1 rounded-full">
                                            {t('Paso', 'Step')} {step.step}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => navigate(step.path)}
                                            className="w-9 h-9 rounded-full bg-princonser-purple text-white flex items-center justify-center hover:bg-princonser-purple-dark transition-colors"
                                            aria-label={step.title}
                                        >
                                            →
                                        </button>
                                    </div>
                                </article>
                            ))}
                        </div>
                    </div>

                    <div className="mt-12">
                        <WaveDivider fillClass="text-princonser-purple" />
                    </div>
                </section>

                {/* ——— Libros (morado) ——— */}
                <section id="libros-section" className="bg-princonser-books py-14 sm:py-16 text-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8">
                            {/* Cada libro incluye */}
                            <div className="border-r-0 lg:border-r border-dashed border-white/30 lg:pr-8">
                                <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                                    <div>
                                        <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                                            <span>📚</span>
                                            {t('Cada libro incluye', 'Each book includes')}
                                        </h2>
                                        <p className="text-white/75 text-sm mt-1">{t('Mucho más que lectura', 'Much more than reading')}</p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/innovaciones')}
                                        className="px-4 py-2 rounded-full border border-white/60 text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
                                    >
                                        {t('Ver cómo funciona', 'See how it works')} →
                                    </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    {bookIncludes.map((card) => (
                                        <div key={card.title} className="bg-white rounded-2xl p-5 text-princonser-navy shadow-lg">
                                            <div className="text-3xl mb-3">{card.icon}</div>
                                            <h3 className="font-bold text-sm mb-2">{card.title}</h3>
                                            <p className="text-xs text-princonser-text-body leading-relaxed">{card.desc}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Destacados */}
                            <div>
                                <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
                                    <div>
                                        <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                                            <span>⭐</span>
                                            {t('Libros destacados', 'Featured books')}
                                        </h2>
                                        <p className="text-white/75 text-sm mt-1">
                                            {t('Historias que enseñan e inspiran', 'Stories that teach and inspire')}
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => navigate('/libros/1')}
                                        className="px-4 py-2 rounded-full border border-white/60 text-sm font-medium hover:bg-white/10 transition-colors whitespace-nowrap"
                                    >
                                        {t('Explorar libros', 'Explore books')} →
                                    </button>
                                </div>
                                <div className="grid grid-cols-3 gap-3 sm:gap-4">
                                    {featuredBooks.map((book) => (
                                        <button
                                            key={book.num}
                                            type="button"
                                            onClick={() => navigate(book.path)}
                                            className="group text-center"
                                        >
                                            <div className="rounded-2xl overflow-hidden shadow-xl border-2 border-white/20 group-hover:scale-[1.03] transition-transform aspect-[3/4] bg-princonser-purple-darker/40">
                                                <img
                                                    src={book.img}
                                                    alt={book.title}
                                                    className="w-full h-full object-cover"
                                                />
                                            </div>
                                            <p className="mt-2 text-xs sm:text-sm font-semibold">{book.title}</p>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <WaveDivider fillClass="text-princonser-purple-soft" flip />

                {/* ——— Por qué familias y escuelas ——— */}
                <section className="bg-princonser-purple-soft py-16 sm:py-20 relative overflow-hidden">
                    <span className="absolute top-6 right-10 text-yellow-300 text-xl opacity-60" aria-hidden>✦</span>
                    <span className="absolute bottom-20 left-8 text-princonser-purple-light text-lg opacity-50" aria-hidden>✦</span>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-princonser-navy">
                                {t('Por qué familias y escuelas eligen', 'Why families and schools choose')}{' '}
                                <span className="text-princonser-purple">Princonser Kids</span>
                                <span className="ml-1" aria-hidden>💜</span>
                            </h2>
                            <p className="text-princonser-text-body mt-3 max-w-2xl mx-auto text-sm sm:text-base">
                                {t(
                                    'Un método que convierte la sabiduría y el propósito en aprendizajes prácticos, visibles y medibles.',
                                    'A method that turns wisdom and purpose into practical, visible and measurable learning.'
                                )}
                            </p>
                        </div>

                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
                            {whyFeatures.map((f) => (
                                <div key={f.title} className="bg-white rounded-2xl p-5 shadow-md text-center">
                                    <div className={`inline-flex w-12 h-12 rounded-full items-center justify-center text-xl mb-3 ${f.color}`}>
                                        {f.icon}
                                    </div>
                                    <h3 className="font-bold text-princonser-navy text-sm mb-1">{f.title}</h3>
                                    <p className="text-xs text-princonser-text-body">{f.desc}</p>
                                </div>
                            ))}
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Padres */}
                            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-princonser-purple-muted flex items-center justify-center text-princonser-purple">♥</div>
                                    <h3 className="text-xl font-bold text-princonser-purple">{t('Para padres', 'For parents')}</h3>
                                </div>
                                <div className="flex gap-4 mb-4">
                                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-princonser-purple-muted to-pink-50 flex items-center justify-center text-4xl flex-shrink-0">
                                        👨‍👩‍👦
                                    </div>
                                    <p className="text-sm text-princonser-text-body leading-relaxed">
                                        {t(
                                            'Herramientas para criar con valores, diálogo y hábitos de aprendizaje en el hogar, sin depender solo de pantallas.',
                                            'Tools to raise children with values, dialogue and learning habits at home, without relying only on screens.'
                                        )}
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-6">
                                    {parentBenefits.map((b) => (
                                        <div key={b} className="flex items-center gap-2 bg-princonser-purple-muted rounded-xl px-3 py-2 text-xs font-medium text-princonser-text-body">
                                            <span className="text-princonser-purple">✓</span>
                                            {b}
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate('/support-materials')}
                                    className="w-full sm:w-auto px-6 py-2.5 rounded-full border-2 border-princonser-purple text-princonser-purple font-semibold text-sm hover:bg-princonser-purple-muted transition-colors"
                                >
                                    {t('Ver experiencia familiar', 'See family experience')} →
                                </button>
                            </div>

                            {/* Escuelas */}
                            <div className="bg-white rounded-3xl p-6 sm:p-8 shadow-lg">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-10 h-10 rounded-full bg-princonser-purple-muted flex items-center justify-center text-princonser-purple">🎓</div>
                                    <h3 className="text-xl font-bold text-princonser-purple">{t('Para escuelas', 'For schools')}</h3>
                                </div>
                                <div className="flex gap-4 mb-4">
                                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-princonser-purple-muted to-indigo-100 flex items-center justify-center text-4xl flex-shrink-0">
                                        🏫
                                    </div>
                                    <p className="text-sm text-princonser-text-body leading-relaxed">
                                        {t(
                                            'Complementa el currículo con filosofía, retos y evidencias de impacto en el aula y la comunidad educativa.',
                                            'Complement the curriculum with philosophy, challenges and evidence of impact in the classroom and school community.'
                                        )}
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-2 mb-6">
                                    {schoolBenefits.map((b) => (
                                        <div key={b} className="flex items-center gap-2 bg-princonser-purple-muted rounded-xl px-3 py-2 text-xs font-medium text-princonser-text-body">
                                            <span className="text-princonser-purple">✓</span>
                                            {b}
                                        </div>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate('/que-es-princonser')}
                                    className="w-full sm:w-auto px-6 py-2.5 rounded-full border-2 border-princonser-purple text-princonser-purple font-semibold text-sm hover:bg-princonser-purple-muted transition-colors"
                                >
                                    {t('Ver solución para colegios', 'See school solution')} →
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ——— Newsletter ——— */}
                <section className="relative bg-princonser-newsletter py-16 sm:py-20 overflow-hidden">
                    <div className="absolute inset-0 opacity-15 pointer-events-none" aria-hidden>
                        <span className="absolute top-16 left-[8%] text-3xl">✈️</span>
                        <span className="absolute top-24 right-[10%] text-2xl">⭐</span>
                        <span className="absolute bottom-32 left-[12%] text-2xl">📖</span>
                    </div>

                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
                            <div className="relative hidden sm:block">
                                <img
                                    src="/images/infosection/NinosArbol.png"
                                    alt=""
                                    className="rounded-2xl w-full max-w-md mx-auto shadow-2xl"
                                    onError={(e) => {
                                        (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                />
                                <div className="absolute -right-1 sm:right-0 top-1/4 flex flex-col gap-1.5">
                                    {[
                                        { label: t('Salud Mental', 'Mental Health'), color: 'bg-sky-400' },
                                        { label: t('Salud Física', 'Physical Health'), color: 'bg-rose-500' },
                                        { label: t('Salud Social', 'Social Health'), color: 'bg-amber-400' },
                                        { label: t('Salud Financiera', 'Financial Health'), color: 'bg-princonser-purple' },
                                    ].map((book) => (
                                        <div
                                            key={book.label}
                                            className={`${book.color} text-white text-[10px] sm:text-xs font-bold px-2 py-1.5 rounded-md shadow-lg max-w-[5.5rem] leading-tight`}
                                        >
                                            {book.label}
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div className="text-white">
                                <div className="text-5xl mb-4 text-center lg:text-left">📧</div>
                                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold mb-2 text-center lg:text-left">
                                    {t('Mantente al día', 'Stay updated')}{' '}
                                    <span className="text-sky-400">{t('con nuestra Newsletter', 'with our Newsletter')}</span>
                                </h2>
                                <p className="text-white/75/90 text-sm sm:text-base mb-8 text-center lg:text-left">
                                    {t(
                                        'Recibe contenido exclusivo, tips educativos, novedades sobre nuestros libros y recursos gratuitos para el desarrollo integral de tus hijos.',
                                        'Receive exclusive content, educational tips, news about our books and free resources for your children\'s development.'
                                    )}
                                </p>

                                {newsletterSuccess ? (
                                    <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-center border border-white/20">
                                        <div className="text-4xl mb-2">🎉</div>
                                        <p className="text-xl font-bold">{t('¡Bienvenido a nuestra Newsletter!', 'Welcome to our Newsletter!')}</p>
                                        <p className="text-white/75 mt-2 text-sm">
                                            {t('Te notificaremos sobre novedades y contenido exclusivo.', 'We will notify you about news and exclusive content.')}
                                        </p>
                                    </div>
                                ) : (
                                    <form
                                        onSubmit={async (e) => {
                                            e.preventDefault();
                                            if (!newsletterEmail.trim() || !newsletterPrivacy) return;
                                            setNewsletterLoading(true);
                                            setNewsletterError(null);
                                            try {
                                                trackCTAClick('newsletter_form_submit', 'newsletter_section', 'button');
                                                pushToDataLayer({
                                                    event: 'generate_lead',
                                                    form_type: 'newsletter',
                                                    email: newsletterEmail,
                                                });
                                                await waitlistService.joinKickstarter(newsletterEmail.trim());
                                                setNewsletterSuccess(true);
                                                setNewsletterEmail('');
                                                setNewsletterPrivacy(false);
                                            } catch {
                                                setNewsletterError(
                                                    t('Hubo un problema. Inténtalo de nuevo.', 'There was a problem. Please try again.')
                                                );
                                            } finally {
                                                setNewsletterLoading(false);
                                            }
                                        }}
                                        className="space-y-4"
                                    >
                                        <div className="relative">
                                            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-princonser-purple">✉</span>
                                            <input
                                                type="email"
                                                value={newsletterEmail}
                                                onChange={(e) => setNewsletterEmail(e.target.value)}
                                                placeholder={t('tu@email.com', 'your@email.com')}
                                                className="w-full pl-11 pr-4 py-3.5 rounded-xl bg-white text-princonser-navy focus:outline-none focus:ring-2 focus:ring-princonser-purple-light"
                                                required
                                            />
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <input
                                                type="checkbox"
                                                id="newsletter-privacy"
                                                checked={newsletterPrivacy}
                                                onChange={(e) => setNewsletterPrivacy(e.target.checked)}
                                                className="mt-1 w-5 h-5 rounded border-gray-300"
                                                required
                                            />
                                            <label htmlFor="newsletter-privacy" className="text-sm text-white/90 cursor-pointer">
                                                {t('He leído y acepto la ', 'I have read and accept the ')}
                                                <a href="/privacidad" className="text-sky-400 underline" target="_blank" rel="noopener noreferrer">
                                                    {t('política de privacidad', 'privacy policy')}
                                                </a>
                                            </label>
                                        </div>
                                        {newsletterError && <p className="text-sm text-rose-300">{newsletterError}</p>}
                                        <button
                                            type="submit"
                                            disabled={newsletterLoading}
                                            className="w-full inline-flex items-center justify-center gap-2 py-4 rounded-full font-bold text-white bg-princonser-cta hover:opacity-95 shadow-lg disabled:opacity-70 transition-all"
                                        >
                                            {newsletterLoading ? t('Enviando…', 'Sending…') : t('¡Suscribirme!', 'Subscribe!')}
                                            <span>→</span>
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>

                        <div className="mt-12 pt-8 border-t border-princonser-purple/30 grid grid-cols-2 lg:grid-cols-4 gap-6">
                            {newsletterBenefits.map((b, i) => (
                                <div
                                    key={b.label}
                                    className={`flex flex-col sm:flex-row items-center gap-3 text-center sm:text-left ${
                                        i < newsletterBenefits.length - 1 ? 'lg:border-r lg:border-princonser-purple/25' : ''
                                    }`}
                                >
                                    <div className={`w-10 h-10 rounded-full ${b.color} flex items-center justify-center text-lg flex-shrink-0`}>
                                        {b.icon}
                                    </div>
                                    <span className="text-xs sm:text-sm text-white/90 font-medium">{b.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default Home;
